import classNames from 'classnames';
import { cloneElement, isValidElement, useCallback, useMemo, useRef, useState } from 'react';

import ActionMenu from 'common/ActivityChip/ActionMenu';
import { getActivityActions } from 'common/ActivityChip/ActionMenu/actionMenuFactory';
import { useActivityMutations } from 'common/ActivityChip/ActionMenu/useActivityMutation';
import { useChipModalOpener } from 'common/ActivityChip/useMakeChipModalOpener';
import { dispatchExtraActivityTimelineManagementClose } from 'common/ExtraActivityTimelineManagement/events';
import { formatTime, getDurationBetweenTimestamps } from 'utils/datetime';

import styles from 'common/ActivityChip/styles.module.css';

const getCompactStationCodes = ({ raw, subline }) => {
	const [fallbackFromStation, fallbackToStation] = (subline ?? '')
		.split('→')
		.map((part) => part.trim());

	return {
		fromStation: raw?.fromStation?.code ?? fallbackFromStation ?? '...',
		toStation: raw?.toStation?.code ?? fallbackToStation ?? '...',
	};
};

const ActivityLayout = ({
	config,
	timezone,
	isSelected,
	isStartInTimeWindow = true,
	isEndInTimeWindow = true,
	suppressNativeTitle = false,
	extraActivityManagement = null,
	onExtraActivityManagementInteractionChange,
}) => {
	const chipRef = useRef(null);
	const [isHoveringChip, setIsHoveringChip] = useState(false);
	const [isFocusWithinChip, setIsFocusWithinChip] = useState(false);
	const [extraActivityManagementInstanceId, setExtraActivityManagementInstanceId] = useState(null);
	const [isExtraActivityManagementActive, setIsExtraActivityManagementActive] = useState(false);
	const { renderType, header, icon, subline, fromTimestamp, toTimestamp, raw, crewMemberId } =
		config;
	const { fromStation, toStation } = getCompactStationCodes({ raw, subline });

	const openModalForChip = useChipModalOpener();
	const { updateWorkAssignment, deleteWorkAActivity, manualReconciliation, loading } =
		useActivityMutations();

	const activityActions = getActivityActions(
		{ ...raw, renderType, fromTimestamp, toTimestamp, crewMemberId },
		{
			onUpdateWorkAssignment: updateWorkAssignment,
			onDeleteWAActivity: deleteWorkAActivity,
			onManualReconciliation: (data) => manualReconciliation({ ...data, timezone }),
		}
	);

	const chipStyles = classNames(styles.activity, styles.regularActivity, styles[renderType], {
		[styles.selected]: isSelected,
		[styles.openStart]: !isStartInTimeWindow,
		[styles.openEnd]: !isEndInTimeWindow,
		[styles.withExtraActivityManagement]: Boolean(extraActivityManagement),
		[styles.extraActivityManagementActive]: isExtraActivityManagementActive,
	});

	const onChipClick = () => {
		const payload = { ...raw, fromTimestamp, toTimestamp };
		if (crewMemberId) payload.crewMemberId = crewMemberId;

		openModalForChip(renderType, payload);
	};

	const handleExtraActivityManagementInteractionChange = useCallback(
		(isActive) => {
			setIsExtraActivityManagementActive(isActive);
			onExtraActivityManagementInteractionChange?.(isActive);
		},
		[onExtraActivityManagementInteractionChange]
	);

	const enhancedExtraActivityManagement = useMemo(() => {
		if (!isValidElement(extraActivityManagement)) return extraActivityManagement;
		if (typeof extraActivityManagement.type === 'string') return extraActivityManagement;

		return cloneElement(extraActivityManagement, {
			anchorRef: chipRef,
			isVisible: isHoveringChip || isFocusWithinChip,
			onInstanceIdChange: setExtraActivityManagementInstanceId,
			onInteractionActiveChange: handleExtraActivityManagementInteractionChange,
		});
	}, [
		extraActivityManagement,
		handleExtraActivityManagementInteractionChange,
		isFocusWithinChip,
		isHoveringChip,
	]);

	const handleChipMouseEnter = () => {
		dispatchExtraActivityTimelineManagementClose(extraActivityManagementInstanceId);
		setIsHoveringChip(true);
	};

	return (
		<div
			ref={chipRef}
			title={suppressNativeTitle ? undefined : header}
			className={chipStyles}
			data-testid={header ? `activity-chip-${header}` : undefined}
			onClick={onChipClick}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget)) {
					setIsFocusWithinChip(false);
				}
			}}
			onFocus={() => setIsFocusWithinChip(true)}
			onMouseEnter={handleChipMouseEnter}
			onMouseLeave={() => setIsHoveringChip(false)}
		>
			<div className={styles.actionMenuSlot}>
				<ActionMenu items={activityActions} isLoading={loading} />
			</div>
			{extraActivityManagement && (
				<div className={styles.extraActivityManagementSlot}>{enhancedExtraActivityManagement}</div>
			)}

			<div className={styles.header}>
				<div className={styles.activityIcon}>{icon}</div>
				<div className={styles.headerText}>{header}</div>
			</div>

			<div className={styles.subline}>{subline}</div>

			<div className={styles.compactActivityRoute} data-testid="activity-cramped-variant">
				<div className={styles.compactActivityIcon}>{icon}</div>
				<div className={styles.compactActivityStation}>{fromStation}</div>
				<div className={styles.compactActivityStation}>{toStation}</div>
				<div className={`${styles.duration} ${styles.compactActivityDuration}`}>
					{getDurationBetweenTimestamps(fromTimestamp, toTimestamp)}
				</div>
			</div>

			<div className={styles.timeRow}>
				<div className={styles.regTime}>{`${formatTime(fromTimestamp, { timezone })}`}</div>
				<div className={styles.duration}>
					{getDurationBetweenTimestamps(fromTimestamp, toTimestamp)}
				</div>
				<div
					className={`${styles.regTime} ${styles.right}`}
				>{`${formatTime(toTimestamp, { timezone })}`}</div>
			</div>
		</div>
	);
};

export default ActivityLayout;
